
import React from 'react';
import { Newspaper, Route, Tent, Heart, FolderOpen, Compass, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { SidebarMode } from '../../types/sidebar';

export const RAIL_COLLAPSED_WIDTH = 56;
export const RAIL_EXPANDED_WIDTH = 208;

interface SidebarRailProps {
  sidebarMode: SidebarMode;
  panelOpen: boolean;
  onSelectMode: (mode: SidebarMode) => void;
  newsCount: number;
  /** «Nytt i Mitt»: antall samarbeidsmapper m/ usett innhold/chat fra andre medlemmer. */
  myContentCount?: number;
  expanded: boolean;
  onToggleExpand: () => void;
  copilotEnabled?: boolean;
}

const railButtons: { mode: SidebarMode; icon: typeof Newspaper; labelKey: string; activeClass: string }[] = [
  { mode: 'discover',  icon: Compass,   labelKey: 'nav.discover',      activeClass: 'bg-amber-500 text-stone-900' },
  { mode: 'news',      icon: Newspaper, labelKey: 'sidebar.news',      activeClass: 'bg-amber-500 text-stone-900' },
  { mode: 'routes',    icon: Route,     labelKey: 'nav.routes',        activeClass: 'bg-emerald-500 text-stone-900' },
  { mode: 'spots',     icon: Tent,      labelKey: 'nav.spots',         activeClass: 'bg-emerald-500 text-stone-900' },
  { mode: 'favorites', icon: Heart,     labelKey: 'nav.favorites',     activeClass: 'bg-red-500 text-white' },
];

const SidebarRail: React.FC<SidebarRailProps> = ({
  sidebarMode,
  panelOpen,
  onSelectMode,
  newsCount,
  myContentCount = 0,
  expanded,
  onToggleExpand,
  copilotEnabled = false,
}) => {
  const { t } = useTranslation();

  const buttonLayout = expanded
    ? 'mx-1.5 px-3 gap-3 justify-start'
    : 'mx-auto w-11 justify-center';

  return (
    <div
      className="hidden sm:flex flex-col glass py-4 gap-2 z-mapOverlay absolute left-0 bottom-0 shadow-2xl"
      style={{
        top: 'calc(88px + var(--safe-area-top, 0px))',
        borderTop: 'none',
        width: expanded ? RAIL_EXPANDED_WIDTH : RAIL_COLLAPSED_WIDTH,
        transition: 'width 200ms ease',
      }}
    >
      {railButtons.map(({ mode, icon: Icon, labelKey, activeClass }) => {
        const isActive = panelOpen && sidebarMode === mode;
        return (
          <button
            key={mode}
            onClick={() => onSelectMode(mode)}
            aria-label={t(labelKey)}
            aria-pressed={isActive}
            className={`relative h-11 flex items-center rounded-xl transition-colors ${buttonLayout} ${
              isActive
                ? activeClass
                : 'text-[var(--text-secondary)] hover:bg-[var(--bg-surface)] hover:text-[var(--text-primary)]'
            }`}
            title={!expanded ? t(labelKey) : undefined}
          >
            <Icon size={18} className="shrink-0" fill={mode === 'favorites' && isActive ? 'currentColor' : 'none'} />
            {expanded && (
              <span className="text-sm font-medium whitespace-nowrap">{t(labelKey)}</span>
            )}
            {mode === 'news' && newsCount > 0 && !isActive && (
              <span
                className={`absolute min-w-[18px] h-[18px] rounded-full bg-amber-500 border-2 border-[var(--bg-surface-alt)] flex items-center justify-center ${
                  expanded ? 'right-2 top-1/2 -translate-y-1/2' : '-top-1 -right-1'
                }`}
              >
                <span className="text-3xs font-black text-stone-900 leading-none">{newsCount > 99 ? '99+' : newsCount}</span>
              </span>
            )}
          </button>
        );
      })}

      <div className={`border-t border-[var(--border-secondary)] my-1 ${expanded ? 'mx-3' : 'w-6 mx-auto'}`} />

      {copilotEnabled && (
        <button
          onClick={() => onSelectMode('copilot')}
          aria-label={t('nav.copilot', 'Reise-copilot')}
          aria-pressed={panelOpen && sidebarMode === 'copilot'}
          className={`relative h-11 flex items-center rounded-xl transition-colors ${buttonLayout} ${
            panelOpen && sidebarMode === 'copilot'
              ? 'bg-emerald-500 text-stone-900'
              : 'text-[var(--text-secondary)] hover:bg-[var(--bg-surface)] hover:text-[var(--text-primary)]'
          }`}
          title={!expanded ? t('nav.copilot', 'Reise-copilot') : undefined}
        >
          <Sparkles size={18} className="shrink-0" />
          {expanded && <span className="text-sm font-medium whitespace-nowrap">{t('nav.copilot', 'Copilot')}</span>}
          {!(panelOpen && sidebarMode === 'copilot') && (
            <span className={`absolute rounded-full bg-emerald-500/20 text-emerald-500 text-[8px] font-black px-1 leading-tight ${expanded ? 'right-2 top-1/2 -translate-y-1/2' : '-top-1 -right-1'}`}>TEST</span>
          )}
        </button>
      )}

      <button
        onClick={() => onSelectMode('mycontent')}
        aria-label={t('myContent.title', 'Mitt innhold')}
        aria-pressed={panelOpen && sidebarMode === 'mycontent'}
        className={`relative h-11 flex items-center rounded-xl transition-colors ${buttonLayout} ${
          panelOpen && sidebarMode === 'mycontent'
            ? 'bg-sky-500 text-white'
            : 'text-[var(--text-secondary)] hover:bg-[var(--bg-surface)] hover:text-[var(--text-primary)]'
        }`}
        title={!expanded ? t('myContent.title', 'Mitt innhold') : undefined}
      >
        <FolderOpen size={18} className="shrink-0" />
        {expanded && (
          <span className="text-sm font-medium whitespace-nowrap">{t('myContent.title', 'Mitt innhold')}</span>
        )}
        {myContentCount > 0 && !(panelOpen && sidebarMode === 'mycontent') && (
          <span
            className={`absolute min-w-[18px] h-[18px] rounded-full bg-sky-500 border-2 border-[var(--bg-surface-alt)] flex items-center justify-center ${
              expanded ? 'right-2 top-1/2 -translate-y-1/2' : '-top-1 -right-1'
            }`}
          >
            <span className="text-3xs font-black text-white leading-none">{myContentCount > 99 ? '99+' : myContentCount}</span>
          </span>
        )}
      </button>

      <button
        onClick={onToggleExpand}
        aria-label={expanded ? t('sidebar.collapseRail', 'Skjul tekst') : t('sidebar.expandRail', 'Vis tekst')}
        aria-expanded={expanded}
        className={`mt-auto h-11 flex items-center rounded-xl transition-colors text-[var(--text-secondary)] hover:bg-[var(--bg-surface)] hover:text-[var(--text-primary)] ${buttonLayout}`}
        title={expanded ? t('sidebar.collapseRail', 'Skjul tekst') : t('sidebar.expandRail', 'Vis tekst')}
      >
        {expanded ? <ChevronLeft size={18} className="shrink-0" /> : <ChevronRight size={18} className="shrink-0" />}
        {expanded && (
          <span className="text-sm font-medium whitespace-nowrap">{t('sidebar.collapseRail', 'Skjul tekst')}</span>
        )}
      </button>
    </div>
  );
};

export default SidebarRail;
